import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { cryptoAPI, portfolioAPI, statsAPI, authAPI } from '../services/api';
import api from '../services/api';
import Layout from '../components/Layout';
import TradingViewChart from '../components/TradingViewChart';
import BotScanner from '../components/BotScanner';
import { usdt } from '../utils/currency';
import './Dashboard.css';

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [prices, setPrices] = useState([]);
  const [allPrices, setAllPrices] = useState([]); // liste complète pour la crypto du jour
  const [botUniverse, setBotUniverse] = useState({ total: 200, coins: [] }); // coins du scanner
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState(null);
  const [tradingStats, setTradingStats] = useState({ totalProfit: 0 });
  const [portfolioStats, setPortfolioStats] = useState({
    tradesWon: 0,
    pending: 0,
    successRate: 0
  });
  const [confirming, setConfirming] = useState(false);
  const [confirmDone, setConfirmDone] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(0);

  // Afficher l'overlay si le capital n'a pas encore été confirmé
  const showCapitalOverlay = user &&
    parseFloat(user.balance) > 0 &&
    !user.capitalConfirmed &&
    !confirmDone;

  const handleConfirmCapital = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      await authAPI.confirmCapital();
      setConfirmDone(true);
      await refreshUser();
    } catch (err) {
      console.error('Confirm capital error:', err);
    } finally {
      setConfirming(false);
    }
  };

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await cryptoAPI.getPrices('usd');
        setPrices(response.data.prices.slice(0, 3)); // Top 3
        setAllPrices(response.data.prices); // liste complète (crypto du jour)
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  // Charger l'univers des coins pour l'animation du scanner du bot
  useEffect(() => {
    api.get('/crypto/bot-universe')
      .then(r => { if (r.data?.coins?.length) setBotUniverse(r.data); })
      .catch(() => {}); // le composant a une liste de secours
  }, []);

  // Fetch referral stats pour le badge de rang
  useEffect(() => {
    const fetchReferralStats = async () => {
      try {
        const response = await api.get('/auth/referral-stats');
        setReferralStats(response.data);
      } catch (error) {
        console.error('Error fetching referral stats:', error);
      }
    };
    fetchReferralStats();
  }, []);

  // Fetch portfolio summary and trading stats
  useEffect(() => {
    const fetchPortfolioSummary = async () => {
      try {
        const [portfolioRes, statsRes] = await Promise.all([
          portfolioAPI.getSummary(),
          statsAPI.get()
        ]);
        setPortfolioStats(portfolioRes.data);
        if (statsRes.data) {
          setTradingStats({
            totalProfit: statsRes.data.totalProfit || 0
          });
        }
      } catch (error) {
        console.error('Error fetching portfolio summary:', error);
      }
    };
    fetchPortfolioSummary();
  }, []);

  const getRankIcon = (rank) => {
    const icons = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇',
      platinum: '💎',
      diamond: '👑'
    };
    return icons[rank] || '🥉';
  };

  const getRankColor = (rank) => {
    const colors = {
      bronze: '#cd7f32',
      silver: '#c0c0c0',
      gold: '#ffd700',
      platinum: '#e5e4e2',
      diamond: '#b9f2ff'
    };
    return colors[rank] || '#cd7f32';
  };

  const formatCurrency = (value) => usdt(value);

  // Fait défiler les noms des filleuls en attente (un seul widget, même
  // s'il y en a plusieurs) — un nom affiché à la fois, change toutes les 4s
  const pendingReferrals = referralStats?.pendingReferrals || [];
  useEffect(() => {
    if (pendingReferrals.length <= 1) return;
    const interval = setInterval(() => {
      setPendingIndex((i) => (i + 1) % pendingReferrals.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [pendingReferrals.length]);

  // Crypto du jour: choisie "au hasard" mais STABLE toute la journée
  // (hash de la date → index dans la liste). Change chaque jour à minuit.
  const dailyCrypto = React.useMemo(() => {
    if (!allPrices.length) return null;
    const dateStr = new Date().toDateString(); // ex: "Sat Jul 11 2026"
    let hash = 0;
    for (const c of dateStr) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
    return allPrices[hash % allPrices.length];
  }, [allPrices]);

  return (
    <Layout>
      <div className="dashboard-page">

        {/* ── Bloc confirmation capital ── compact */}
        {showCapitalOverlay && (
          <section className="cap-banner">
            <div className="cap-banner-left">
              <div className="cap-banner-icon">
                <svg viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="16" fill="#f0b90b" opacity=".2"/>
                  <circle cx="18" cy="18" r="10" fill="#f0b90b"/>
                  <text x="18" y="23" textAnchor="middle" fontSize="12" fontWeight="800" fill="#1a1200">₮</text>
                </svg>
              </div>
              <div className="cap-banner-text">
                <p className="cap-banner-title">{t('dashboard.capitalToInvest')}</p>
                <p className="cap-banner-amount">
                  {formatCurrency(user.balance)}
                </p>
              </div>
            </div>
            <button
              className={`cap-banner-btn${confirming ? ' loading' : ''}`}
              onClick={handleConfirmCapital}
              disabled={confirming}
            >
              {confirming ? <span className="cap-spinner" /> : <span>{t('dashboard.invest')}</span>}
            </button>
            <div className="cap-banner-shimmer"/>
          </section>
        )}

        {/* Portfolio Card */}
        <section className="portfolio-card">
          <div className="portfolio-top">
            <span className="portfolio-label">{t('common.portfolioValue')}</span>
            <div className="portfolio-badges">
              {referralStats && (
                <span
                  className="rank-badge"
                  style={{
                    background: `${getRankColor(referralStats.rank)}20`,
                    borderColor: getRankColor(referralStats.rank),
                    color: getRankColor(referralStats.rank)
                  }}
                  onClick={() => navigate('/profile?section=referral')}
                >
                  {getRankIcon(referralStats.rank)}
                </span>
              )}
              <span className="portfolio-badge">USDT</span>
            </div>
          </div>
          <div className="portfolio-amount-row">
            <span className="portfolio-value">{formatCurrency(user?.balance || 0)}</span>
            <span className={`portfolio-change ${tradingStats.totalProfit >= 0 ? 'positive' : 'negative'}`}>
              {/* parseFloat obligatoire: balance arrive en CHAÎNE ("0.00" est truthy → NaN) */}
              {tradingStats.totalProfit >= 0 ? '+' : ''}{((tradingStats.totalProfit / (parseFloat(user?.balance) || 1)) * 100).toFixed(2)}%
            </span>
          </div>
          <div className="portfolio-actions">
            <button className="action-btn primary" onClick={() => navigate('/trading')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
              </svg>
              <span>{t('nav.trading')}</span>
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/wallet')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
                <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
              </svg>
              <span>{t('nav.wallet')}</span>
            </button>
          </div>
        </section>

        {/* Accueil du nouveau filleul: tant qu'il n'a pas rechargé, on lui
            rappelle qui l'a parrainé et comment le contacter */}
        {referralStats?.referrerPending && (
          <section className="referral-nudge welcome">
            <span className="referral-nudge-icon">🎉</span>
            <div className="referral-nudge-text">
              <p className="referral-nudge-title">{t('dashboard.welcomeReferredTitle')}</p>
              <p className="referral-nudge-body">
                {t('dashboard.welcomeReferredBody', { name: referralStats.referrerPending.username })}
              </p>
              <p className="referral-nudge-contact">
                📧 {referralStats.referrerPending.email
                  ? t('dashboard.welcomeReferredContactLine', { name: referralStats.referrerPending.username, email: referralStats.referrerPending.email })
                  : t('dashboard.welcomeReferredContactLineNoEmail', { name: referralStats.referrerPending.username })}
              </p>
              <div className="referral-nudge-actions">
                <button className="referral-nudge-btn primary" onClick={() => navigate('/wallet')}>
                  {t('dashboard.welcomeReferredRecharge')}
                </button>
                <button className="referral-nudge-btn" onClick={() => navigate('/profile?section=support')}>
                  {t('dashboard.welcomeReferredContact')}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Rappel au parrain: filleul(s) pas encore rechargés — un seul
            widget même s'il y en a plusieurs, les noms défilent */}
        {pendingReferrals.length > 0 && (
          <section className="referral-nudge">
            <span className="referral-nudge-icon">👋</span>
            <div className="referral-nudge-text">
              <p className="referral-nudge-title">{t('dashboard.nudgeReferrerTitle')}</p>
              <p className="referral-nudge-body" key={pendingIndex}>
                {t('dashboard.nudgeReferrerBody', { name: pendingReferrals[pendingIndex].username })}
              </p>
              <p className="referral-nudge-contact" key={`c-${pendingIndex}`}>
                {pendingReferrals[pendingIndex].email || pendingReferrals[pendingIndex].username}
              </p>
            </div>
            {pendingReferrals.length > 1 && (
              <div className="referral-nudge-dots">
                {pendingReferrals.map((_, i) => (
                  <span key={i} className={`referral-nudge-dot ${i === pendingIndex ? 'active' : ''}`} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Quick Stats */}
        <section className="quick-stats">
          <div className="stat-card">
            <div className="stat-icon green">📈</div>
            <div className="stat-info">
              <span className="stat-value">{portfolioStats.tradesWon}</span>
              <span className="stat-label">{t('dashboard.wonTrades')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon yellow">⏳</div>
            <div className="stat-info">
              <span className="stat-value">{portfolioStats.pending}</span>
              <span className="stat-label">{t('dashboard.pending')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue">🎯</div>
            <div className="stat-info">
              <span className="stat-value">{portfolioStats.successRate}%</span>
              <span className="stat-label">{t('dashboard.successRate')}</span>
            </div>
          </div>
        </section>

        {/* Graphique du jour — vraie courbe TradingView d'une crypto tirée chaque jour */}
        {dailyCrypto && (
          <section className="daily-chart-section">
            <div className="section-header">
              <h2>{t('dashboard.dailyChart') || 'Graphique du jour'}</h2>
              <button className="see-all-btn" onClick={() => navigate('/markets')}>
                {t('common.seeAll')} →
              </button>
            </div>
            <div className="daily-chart-card">
              <div className="daily-chart-header">
                <div className={`crypto-icon ${dailyCrypto.symbol.toLowerCase()}`}>
                  {dailyCrypto.symbol.charAt(0)}
                </div>
                <div className="daily-chart-info">
                  <span className="daily-chart-symbol">{dailyCrypto.symbol}/USDT</span>
                  <span className="daily-chart-price">
                    {formatCurrency(dailyCrypto.price)}
                    <span className={`crypto-change ${dailyCrypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                      {dailyCrypto.change24h >= 0 ? '+' : ''}{dailyCrypto.change24h?.toFixed(2)}%
                    </span>
                  </span>
                </div>
              </div>
              <div className="daily-chart-body">
                <TradingViewChart symbol={dailyCrypto.symbol} />
              </div>
            </div>
          </section>
        )}

        {/* Market Overview */}
        <section className="market-section">
          <div className="section-header">
            <h2>{t('dashboard.topCryptos')}</h2>
            <button className="see-all-btn" onClick={() => navigate('/markets')}>
              {t('common.seeAll')} →
            </button>
          </div>

          {loading ? (
            <div className="loading-skeleton">
              <div className="skeleton-item"></div>
              <div className="skeleton-item"></div>
              <div className="skeleton-item"></div>
            </div>
          ) : (
            <div className="market-list">
              {prices.map((crypto) => (
                <div key={crypto.symbol} className="market-item" onClick={() => navigate('/markets')}>
                  <div className="market-left">
                    <div className={`crypto-icon ${crypto.symbol.toLowerCase()}`}>
                      {crypto.symbol.charAt(0)}
                    </div>
                    <div className="crypto-info">
                      <span className="crypto-symbol">{crypto.symbol}</span>
                      <span className="crypto-name">{crypto.symbol === 'BTC' ? 'Bitcoin' : crypto.symbol === 'ETH' ? 'Ethereum' : crypto.symbol}</span>
                    </div>
                  </div>
                  <div className="market-right">
                    <span className="crypto-price">{formatCurrency(crypto.price)}</span>
                    <span className={`crypto-change ${crypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                      {crypto.change24h >= 0 ? '+' : ''}{crypto.change24h?.toFixed(2)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bot Status */}
        <section className="bot-section">
          <div className="bot-card">
            {/* Animated background effects */}
            <div className="bot-bg-effects">
              <div className="scan-line"></div>
              <div className="pulse-ring pulse-1"></div>
              <div className="pulse-ring pulse-2"></div>
              <div className="pulse-ring pulse-3"></div>
              <div className="floating-particle p1"></div>
              <div className="floating-particle p2"></div>
              <div className="floating-particle p3"></div>
              <div className="floating-particle p4"></div>
            </div>

            <div className="bot-content">
              <div className="bot-status">
                {/* Animated SVG Robot */}
                <div className="bot-avatar">
                  <svg viewBox="0 0 100 100" className="robot-svg">
                    {/* Antenna */}
                    <g className="antenna">
                      <line x1="50" y1="15" x2="50" y2="5" stroke="#1e88e5" strokeWidth="3" strokeLinecap="round"/>
                      <circle cx="50" cy="3" r="4" fill="#0ecb81" className="antenna-light"/>
                    </g>

                    {/* Head */}
                    <rect x="25" y="15" width="50" height="45" rx="10" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="2"/>

                    {/* Face screen */}
                    <rect x="30" y="22" width="40" height="30" rx="5" fill="#0b1929"/>

                    {/* Eyes */}
                    <g className="eyes">
                      <circle cx="40" cy="37" r="6" fill="#0ecb81" className="eye left-eye"/>
                      <circle cx="60" cy="37" r="6" fill="#0ecb81" className="eye right-eye"/>
                      <circle cx="40" cy="37" r="2" fill="#fff" className="pupil"/>
                      <circle cx="60" cy="37" r="2" fill="#fff" className="pupil"/>
                    </g>

                    {/* Mouth / Display */}
                    <rect x="38" y="45" width="24" height="4" rx="2" fill="#1e88e5" className="mouth"/>

                    {/* Body */}
                    <rect x="30" y="62" width="40" height="25" rx="5" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="2"/>

                    {/* Chest light */}
                    <circle cx="50" cy="74" r="5" fill="#f0b90b" className="chest-light"/>

                    {/* Arms */}
                    <rect x="15" y="65" width="12" height="6" rx="3" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="1.5" className="arm left-arm"/>
                    <rect x="73" y="65" width="12" height="6" rx="3" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="1.5" className="arm right-arm"/>
                  </svg>

                  {/* Glow effect */}
                  <div className="bot-glow"></div>
                </div>

                <div className="bot-info">
                  <span className="bot-title">{t('dashboard.tradingBot')}</span>
                  <span className="bot-state active">
                    <span className="status-dot"></span>
                    {t('dashboard.botAnalyzing')}
                  </span>
                </div>
              </div>

              <BotScanner
                coins={botUniverse.coins}
                labels={{
                  validated: t('dashboard.botSignalValidated') || 'Signal validé',
                  rejected: t('dashboard.botSignalRejected') || 'Écarté',
                  scanning: t('dashboard.botScanning') || 'Analyse'
                }}
              />

              <p className="bot-message">{t('dashboard.botMessage')}</p>

              <button className="bot-action" onClick={() => navigate('/trading')}>
                <span>{t('dashboard.viewSuggestions')}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
